import { CreateElement, VNode, VNodeData } from 'vue';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { getRange, setRange } from 'selection-ranges';

@Component
export default class ClButtonBase extends Vue {
  /**
   * The component used for the root node.
   */
  @Prop({
    type: String,
    default: 'button'
  })
  public component!: string;

  /**
   * If true, the base button will be disabled.
   */
  @Prop(Boolean)
  public disabled!: boolean;

  /**
   * This property can help a person know which element has the keyboard focus.
   * The class name will be applied when the element gain the focus through a keyboard interaction.
   * It's a polyfill for the CSS :focus-visible feature https://drafts.csswg.org/selectors-4/#the-focus-visible-pseudo.
   * The rational for using this feature is explained here: https://github.com/WICG/focus-visible/blob/master/explainer.md.
   */
  @Prop(String)
  public focusVisibleClass!: string;

  private focusVisible: boolean = Boolean(this.focusVisibleClass);

  @Prop({
    type: Boolean,
    default: false
  })
  public contenteditable!: boolean;

  @Prop({
    default: '',
    type: String
  })
  public value!: string;

  /**
   * Used to control the button's purpose.
   * This property passes the value to the type attribute of the native button component.
   * Valid property values include `button`, `submit`, and `reset`.
   */
  @Prop({
    type: String,
    default: 'button',
    validator: x => ['button', 'submit', 'reset'].includes(x)
  })
  public type!: string;

  private handleBlur(event: FocusEvent): void {
    if (this.focusVisible) {
      this.focusVisible = false;
    }
    this.$emit('blur', event);
  }

  private handleMouseDown(event: MouseEvent): void {
    if (this.focusVisible) {
      this.focusVisible = false;
    }
    this.$emit('mousedown', event);
  }

  /**
   * Handle Input
   * in order to retain the cursor position on each sentence update
   * we need to get the range and reset it to what it was before the update
   */
  handleInput(event: Event) {
    this.$emit('input', (event.target as HTMLElement).innerHTML);
    const node = this.$refs.component;
    const range = getRange(node);
    if (range) {
      // when changing sentence data
      // we inadvertently reset the cursor position to 0
      // this will set the cursor position to
      // what it was before we rehydrate the sentence
      this.$nextTick(() => {
        setRange(node, range);
      });
    }
  }

  public render(h: CreateElement): VNode {
    let component = this.component;

    const data: VNodeData = {
      class: {
        'cl-button-base': true,
        'cl-button-base--editable': this.contenteditable,
        'cl-button-base--focus-visible': this.focusVisible
      },
      attrs: {
        contenteditable: this.contenteditable
          ? String(this.contenteditable)
          : undefined
      },
      on: {
        blur: this.handleBlur,
        click: (event: MouseEvent) => {
          this.$emit('click', event);
        },
        dblclick: (event: MouseEvent) => {
          this.$emit('dblclick', event);
        },
        focus: (event: FocusEvent) => {
          this.$emit('focus', event);
        },
        keydown: (event: KeyboardEvent) => {
          this.$emit('keydown', event);
        },
        keyup: (event: KeyboardEvent) => {
          this.$emit('keyup', event);
        },
        mousedown: this.handleMouseDown,
        mouseover: (event: MouseEvent) => {
          this.$emit('mouseover', event);
        },
        mouseenter: (event: MouseEvent) => {
          this.$emit('mouseenter', event);
        },
        mouseleave: (event: MouseEvent) => {
          this.$emit('mouseleave', event);
        },
        mouseup: (event: MouseEvent) => {
          this.$emit('mouseup', event);
        },
        touchend: (event: TouchEvent) => {
          this.$emit('touchend', event);
        },
        touchstart: (event: TouchEvent) => {
          this.$emit('touchstart', event);
        }
      }
    };

    if (component === 'button') {
      data.attrs!.type = this.type;
      data.attrs!.disabled = this.disabled;
    } else {
      data.attrs!.role = 'button';
    }

    if (this.contenteditable) {
      component = component === 'button' ? 'div' : component;
      data.ref = 'component';
      data.on = {
        blur: (event: Event) => {
          this.$emit('blur', event);
        },
        input: this.handleInput
      };
      data.domProps = {
        innerHTML: this.value
      };
    }

    return h(component, data, this.$slots.default);
  }
}
